/**
 * ValuesCellPill — inline-editable cell for the INSERT VALUES rows grid: the
 * shared click-to-edit `EditableSqlValue` (Enter / blur commits, Escape
 * cancels, value suggestions), wired to `modifyInsertValuesCell`.
 *
 * Free-text edit (decision 7): gates on `!!editContext` alone in the caller
 * (`ValuesRowsGrid`) — no schema snapshot needed, mirroring the shipped
 * row-level add/remove affordances.
 *
 * PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — DML edits.
 */

import type { VqeEditContext } from '../../edit/editContext';
import { toCommitCtx } from '../../edit/editContext';
import { modifyInsertValuesCell } from '../../edit/edits/modifyInsertValuesCell';
import { recordEditFailure, safeExtractStructure } from './insertColumnMapHelpers';
import { classifyValueCellKind, VALUE_CELL_KIND_TONE_CLASS } from './valueCellKind';
import { EditableSqlValue } from '../../../common/components/EditableSqlValue';
import { columnValueSuggestions } from '../../../common/utils/sqlValueSuggestions';
import { findColumn, resolveWrittenTableColumns } from '../update/assignmentColumns';

export interface ValuesCellPillProps {
  /** Shared root, e.g. `vqe-insert-values-cell` — testids append `-text-R-C` / `-input-R-C`. */
  readonly testIdPrefix: string;
  readonly text: string;
  readonly rowIndex: number;
  readonly cellIndex: number;
  readonly editContext: VqeEditContext;
  /** The cell's column (from the INSERT column list) — types the input's suggestions. */
  readonly columnName?: string;
}

export function ValuesCellPill({
  testIdPrefix,
  text,
  rowIndex,
  cellIndex,
  editContext,
  columnName,
}: ValuesCellPillProps) {
  const kind = classifyValueCellKind(text);
  return (
    <EditableSqlValue
      text={text}
      onCommit={(value) => {
        modifyInsertValuesCell(rowIndex, cellIndex, value, toCommitCtx(editContext), {
          structure: safeExtractStructure(editContext),
        }).catch(recordEditFailure);
      }}
      // The engine's functions, typed by the cell's column — no column names:
      // VALUES can't reference the table's columns (MySQL's earlier-column
      // reference is the one exception, too narrow to offer).
      getSuggestions={() => {
        const table = resolveWrittenTableColumns(editContext, 'insert');
        return columnValueSuggestions(table.engine, null, columnName ? findColumn(table, columnName) : undefined);
      }}
      testId={`${testIdPrefix}-text-${rowIndex}-${cellIndex}`}
      inputTestId={`${testIdPrefix}-input-${rowIndex}-${cellIndex}`}
      ariaLabel="Edit VALUES cell"
      className={`block w-full truncate text-left font-mono hover:underline ${VALUE_CELL_KIND_TONE_CLASS[kind]}`}
      buttonProps={{ 'data-cell-kind': kind }}
      inputClassName="w-full py-0.5"
      trackEvent={'feature:vqe_value_suggestion_accept'}
    />
  );
}
